import { h } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";
import { createPortal } from "preact/compat";

// How far below the fold the main add to bag button has to be before the mobile
// bar takes over.
const TRIGGER_BUFFER = 80;

// The pinned desktop header plus the site border above it, which covers the top
// of the viewport — the button is out of sight once it passes under it.
const PINNED_HEADER_HEIGHT = 85;
const TOP_MARGIN = -PINNED_HEADER_HEIGHT;

// How much of the section after the product has to be in view before the bar
// stands down.
const EXIT_BUFFER = 250;

const IMAGE_SIZE = 64;

const SUPPRESS_EVENT = "sticky-atc:suppress";

/**
 * Hides the bar while anything on the page has its own add to bag button for a
 * different product. The bar stays hidden until every source has released it.
 * @param {Element} source - Identifies the caller, so one releasing doesn't
 *   bring the bar back while another still holds it.
 */
export const suppressStickyAtc = (source, suppress) =>
  document.dispatchEvent(
    new CustomEvent(SUPPRESS_EVENT, { detail: { source, suppress } }),
  );

// Multi-select forms add a cart line per value of the last option (usually
// Size), so only the options chosen for the selection as a whole — Color —
// describe what the bar's total covers.
const optionValues = (variant, isMultiSelect) => {
  if (!variant || variant.product?.helpers.hasOnlyDefaultVariant) return [];

  const options = isMultiSelect
    ? variant.selectedOptions.slice(0, -1)
    : variant.selectedOptions;

  return options.map(({ value }) => value);
};

export default function StickyAtc({
  triggerRef,
  variant,
  isMultiSelect,
  label,
  disabled,
  onAdd,
}) {
  // Whether the main add to bag button is off screen, and whether it went off
  // the top rather than being below the fold — CSS decides which of the two the
  // desktop and mobile bars react to.
  const [offscreen, setOffscreen] = useState(false);
  const [scrolledPast, setScrolledPast] = useState(false);
  const [pastProduct, setPastProduct] = useState(false);
  const [suppressed, setSuppressed] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        const isOffscreen = !entry.isIntersecting;

        // rootMargin puts the root's top edge at -TOP_MARGIN.
        setScrolledPast(
          isOffscreen && entry.boundingClientRect.bottom < -TOP_MARGIN,
        );
        setOffscreen(isOffscreen);
      },
      {
        rootMargin: `${TOP_MARGIN}px ${TRIGGER_BUFFER}px ${TRIGGER_BUFFER}px`,
      },
    );
    observer.observe(trigger);

    return () => observer.disconnect();
  }, [triggerRef]);

  // The bar belongs to the product section, plus a style-about section directly
  // after it; it stands down once EXIT_BUFFER of whatever follows them is in view.
  useEffect(() => {
    const section = triggerRef.current?.closest(".shopify-section");
    if (!section) return;

    const next = section.nextElementSibling;
    const lastSection = next?.matches(".style-about") ? next : section;
    const following =
      lastSection.nextElementSibling ||
      document.querySelector(".shopify-section-group-footer-group");
    if (!following) return;

    const observer = new IntersectionObserver(
      ([entry]) =>
        setPastProduct(
          entry.isIntersecting || entry.boundingClientRect.bottom < 0,
        ),
      { rootMargin: `0px 0px -${EXIT_BUFFER}px 0px` },
    );
    observer.observe(following);

    return () => observer.disconnect();
  }, [triggerRef]);

  useEffect(() => {
    const sources = new Set();
    const onSuppress = ({ detail: { source, suppress } }) => {
      if (suppress) sources.add(source);
      else sources.delete(source);
      setSuppressed(sources.size > 0);
    };

    document.addEventListener(SUPPRESS_EVENT, onSuppress);
    return () => document.removeEventListener(SUPPRESS_EVENT, onSuppress);
  }, []);

  // Published so other fixed widgets can clear the mobile bar.
  useEffect(() => {
    const bar = ref.current;
    if (!bar) return;

    const observer = new ResizeObserver(() =>
      document.documentElement.style.setProperty(
        "--sticky-atc-height",
        `${bar.offsetHeight}px`,
      ),
    );
    observer.observe(bar);

    return () => {
      observer.disconnect();
      document.documentElement.style.removeProperty("--sticky-atc-height");
    };
  }, []);

  const hidden = pastProduct || suppressed;
  const image = variant?.image || variant?.product?.featuredImage;
  const title = variant?.product?.title;
  const values = optionValues(variant, isMultiSelect);

  return createPortal(
    <div
      className={[
        "sticky-atc",
        !hidden && offscreen && "sticky-atc--offscreen",
        !hidden && scrolledPast && "sticky-atc--scrolled-past",
      ]
        .filter(Boolean)
        .join(" ")}
      ref={ref}
    >
      {image && (
        <img
          className="sticky-atc__image"
          src={window.getSizedImageUrl(image.url, `${IMAGE_SIZE * 2}x`)}
          alt={image.altText || title || ""}
          width={IMAGE_SIZE}
          height={IMAGE_SIZE}
          loading="lazy"
        />
      )}
      <div className="sticky-atc__details">
        {title && <span className="sticky-atc__title">{title}</span>}
        {values.length > 0 && (
          <span className="sticky-atc__options">
            {values.map((value) => (
              <span className="sticky-atc__option" key={value}>
                {value}
              </span>
            ))}
          </span>
        )}
      </div>
      <button
        className="sticky-atc__atc button button--black"
        onClick={onAdd}
        disabled={disabled}
        type="button"
        dangerouslySetInnerHTML={{ __html: label }}
      ></button>
    </div>,
    document.body,
  );
}
